/* ServicesTab — comprehensive ZHELTA Services dashboard for super-admin.
 *
 * Five sections, all driven by the new `/api/admin/services/*` endpoints:
 *   1. Stats strip      — total products, stock health, avg price, featured count
 *   2. Top viewed       — re-uses TopProductsWidget for parity with Overview tab
 *   3. Categories       — views per category + unique-visitor share
 *   4. Recent activity  — rolling feed of the last 30 PDP views
 *   5. Catalog table    — full catalog with adjusted price & stock health
 *
 * The pricing rule is also surfaced as plaintext so any new admin can read
 * exactly what surcharge logic the customer sees on /services without
 * grepping the code.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Package, Layers, Eye, Users, Star, AlertTriangle, ExternalLink,
  RefreshCw, ShoppingBag, Tag, BadgeCheck, BarChart3, Activity,
  CheckCircle2, XCircle, Search, Download, Mail, Send, Clock,
} from 'lucide-react';
import TopProductsWidget from './TopProductsWidget';
import StripeSyncStatusWidget from './StripeSyncStatusWidget';
import StripeHealthCheckPanel from './StripeHealthCheckPanel';
import StripeWebhookLogPanel from './StripeWebhookLogPanel';
import DripPerformancePanel from './DripPerformancePanel';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const fmt$ = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtAgo = (iso) => {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

export default function ServicesTab() {
  const [catalog, setCatalog] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [activity, setActivity] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [stockFilter, setStockFilter] = useState('all'); // all | in_stock | low | out

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b, a] = await Promise.all([
        fetch(`${API}/admin/services/catalog`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/admin/services/category-breakdown?days=${days}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/admin/services/recent-activity?limit=30`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
      ]);
      if (c) setCatalog(c);
      if (b) setBreakdown(b.rows || []);
      if (a) setActivity(a.rows || []);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // CSV export — fetch with credentials, then trigger a download from the
  // Blob so the browser respects the server's Content-Disposition filename.
  const downloadCsv = useCallback(async (path, fallbackName) => {
    try {
      const r = await fetch(`${API}${path}`, { credentials: 'include' });
      if (!r.ok) return;
      const blob = await r.blob();
      const headerName = (r.headers.get('content-disposition') || '')
        .match(/filename="([^"]+)"/)?.[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = headerName || fallbackName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  }, []);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    return catalog.rows.filter((p) => {
      if (filterCat !== 'all' && p.category !== filterCat) return false;
      if (stockFilter === 'in_stock' && p.stock <= 0) return false;
      if (stockFilter === 'low' && !(p.stock > 0 && p.stock <= (catalog.pricing_rule.low_stock_threshold || 5))) return false;
      if (stockFilter === 'out' && p.stock > 0) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.slug.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [catalog, filterCat, stockFilter, search]);

  const summary = catalog?.summary || {};
  const rule = catalog?.pricing_rule || {};
  const links = catalog?.links || {};
  const maxCatViews = Math.max(1, ...breakdown.map((b) => b.views || 0));

  return (
    <div className="space-y-8" data-testid="admin-services-tab">
      {/* ===== Header ===== */}
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-[#a8871a]">
            ZHELTA Services
          </span>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-1 text-[#0e0e10]">
            E-store control center
          </h2>
          <p className="text-[13px] text-[#6E6E73] mt-1">
            Live catalog · view analytics · pricing rule · cart-central admin link
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="services-tab-period"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 bg-white outline-none focus:border-[#c9a227]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="services-tab-refresh"
            className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] bg-white hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => downloadCsv('/admin/services/catalog.csv', 'catalog.csv')}
            data-testid="services-tab-export-catalog"
            className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] bg-white hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
            title="Download the live catalog snapshot as CSV"
          >
            <Download className="w-3 h-3" /> Catalog CSV
          </button>
          <button
            type="button"
            onClick={() => downloadCsv(`/admin/services/recent-activity.csv?days=${days}`, `activity-${days}d.csv`)}
            data-testid="services-tab-export-activity"
            className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] bg-white hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
            title={`Download last ${days} days of PDP views as CSV`}
          >
            <Download className="w-3 h-3" /> Activity CSV
          </button>
          {links.store_public && (
            <a
              href={links.store_public}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-tab-open-public"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
            >
              View live store <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {links.store_admin && (
            <a
              href={links.store_admin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-tab-open-admin"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#0e0e10] text-white hover:bg-[#1a1a1d] transition-colors"
            >
              Cart-central admin <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </header>

      {/* ===== Stats strip ===== */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Package}     label="Total products"   value={summary.total ?? '—'}      hint={`${summary.featured ?? 0} featured`} />
        <Stat icon={CheckCircle2}label="In stock"         value={summary.in_stock ?? '—'}   hint={`${summary.free_items ?? 0} listed Free`} tone="ok" />
        <Stat icon={AlertTriangle}label="Low / out of stock" value={(summary.low_stock ?? 0) + (summary.out_of_stock ?? 0)} hint={`${summary.low_stock ?? 0} low · ${summary.out_of_stock ?? 0} out`} tone={(summary.low_stock || summary.out_of_stock) ? 'warn' : 'mute'} />
        <Stat icon={Tag}         label="Avg adjusted price" value={fmt$(summary.avg_adjusted_price)} hint={`+ $${rule.handling_fee ?? 45} over $${rule.fee_threshold ?? 700}`} />
      </section>

      {/* ===== Pricing rule banner ===== */}
      <section
        data-testid="services-pricing-rule"
        className="rounded-2xl border border-[#F0EDE5] bg-[#fff8e7] p-4 md:p-5 flex items-start gap-3"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#a8871a] shrink-0">
          <BadgeCheck className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Pricing rule</div>
          <p className="text-[13.5px] font-semibold text-[#0e0e10] mt-1 leading-snug">
            {rule.explanation || 'Pricing rule not loaded'}
          </p>
        </div>
      </section>

      {/* ===== Low-stock digest card ===== */}
      <LowStockDigestCard threshold={rule.low_stock_threshold ?? 5} />

      {/* ===== Stripe sync status (Systems services → Stripe Products/Prices) ===== */}
      <StripeSyncStatusWidget />

      <StripeHealthCheckPanel />

      {/* ===== Stripe webhook event log (live tail of Stripe → ZHELTA pipeline) ===== */}
      <StripeWebhookLogPanel />

      {/* ===== Drip performance (abandoned cart funnel + revenue attribution) ===== */}
      <DripPerformancePanel />

      {/* ===== Top viewed (re-use widget) ===== */}
      <TopProductsWidget />

      {/* ===== Category breakdown + Recent activity (side-by-side on desktop) ===== */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="services-categories">
          <header className="px-5 py-4 bg-[#f8f8fa] border-b border-gray-100 flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#c9a227]" />
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">Last {days} days</div>
              <div className="text-[14px] font-extrabold text-[#0e0e10]">Views by category</div>
            </div>
          </header>
          <div className="p-4">
            {breakdown.length === 0 ? (
              <div className="text-center py-8 text-[12px] text-[#6E6E73]">No views in this period yet.</div>
            ) : (
              <ul className="space-y-3" data-testid="services-categories-list">
                {breakdown.map((b) => (
                  <li key={b.category}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12.5px] font-extrabold text-[#0e0e10] capitalize">
                        {b.category.replace('-', ' ')}
                      </span>
                      <span className="text-[12px] font-bold tabular-nums text-[#0e0e10]">
                        {b.views} view{b.views === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F5F5F7] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.round((b.views / maxCatViews) * 100)}%`,
                          background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)',
                        }}
                      />
                    </div>
                    <div className="text-[11px] text-[#6E6E73] mt-1">
                      {b.unique_visitors} unique · {b.products} product{b.products === 1 ? '' : 's'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="services-activity">
          <header className="px-5 py-4 bg-[#f8f8fa] border-b border-gray-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#c9a227]" />
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">Live feed</div>
              <div className="text-[14px] font-extrabold text-[#0e0e10]">Recent PDP views</div>
            </div>
          </header>
          <div className="max-h-[380px] overflow-y-auto">
            {activity.length === 0 ? (
              <div className="text-center py-10 text-[12px] text-[#6E6E73]">
                Once a visitor opens a product page, you'll see it stream in here.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {activity.map((row, idx) => (
                  <li
                    key={`${row.slug}-${row.ts}-${idx}`}
                    data-testid={`activity-row-${idx}`}
                    className="px-5 py-2.5 flex items-center gap-3 hover:bg-[#fff8e7]/40 transition-colors"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#fff8e7] text-[#a8871a] shrink-0">
                      <Eye className="h-3.5 w-3.5" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <a
                          href={`/services/p/${encodeURIComponent(row.slug)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[13px] font-extrabold text-[#0e0e10] truncate hover:text-[#a8871a] transition-colors"
                        >
                          {row.name}
                        </a>
                        <span className="text-[11px] text-[#9aa0a6] font-medium tabular-nums shrink-0">
                          {fmtAgo(row.ts)}
                        </span>
                      </div>
                      <div className="text-[11px] text-[#6E6E73] flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-[#9aa0a6]">{row.ip || 'unknown'}</span>
                        <span className="text-[#9aa0a6]">·</span>
                        <span>{row.device || 'Other'}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* ===== Catalog table ===== */}
      <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="services-catalog">
        <header className="px-5 py-4 bg-[#f8f8fa] border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-[#c9a227]" />
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">Live catalog</div>
              <div className="text-[14px] font-extrabold text-[#0e0e10]">
                {catalog ? `${filtered.length} of ${catalog.rows.length} products` : 'Loading…'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9aa0a6]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or slug"
                data-testid="services-catalog-search"
                className="pl-9 pr-3 py-1.5 text-[12px] rounded-full border border-[#E5E5E7] bg-white outline-none focus:border-[#c9a227] w-[200px]"
              />
            </div>
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              data-testid="services-catalog-category"
              className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 bg-white outline-none focus:border-[#c9a227]"
            >
              <option value="all">All categories</option>
              {(summary.categories || []).map((c) => (
                <option key={c} value={c}>{c.replace('-', ' ')}</option>
              ))}
            </select>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              data-testid="services-catalog-stock"
              className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 bg-white outline-none focus:border-[#c9a227]"
            >
              <option value="all">Any stock</option>
              <option value="in_stock">In stock</option>
              <option value="low">Low stock</option>
              <option value="out">Out of stock</option>
            </select>
          </div>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#FAFAFA] text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#6E6E73]">
              <tr>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-right px-4 py-2.5">Adjusted price</th>
                <th className="text-right px-4 py-2.5">Raw price</th>
                <th className="text-center px-4 py-2.5">Stock</th>
                <th className="text-center px-4 py-2.5">Rating</th>
                <th className="text-right px-4 py-2.5">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-testid="services-catalog-rows">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-[12px] text-[#6E6E73]">
                    {loading ? 'Loading catalog…' : 'No products match your filters.'}
                  </td>
                </tr>
              ) : filtered.map((p) => (
                <tr key={p.slug} className="hover:bg-[#fff8e7]/30" data-testid={`catalog-row-${p.slug}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {p.image ? (
                        <img src={p.image} alt="" className="h-9 w-9 rounded-lg object-cover bg-[#FAFAFA] border border-[#F0EDE5]" />
                      ) : (
                        <span className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-[#FAFAFA] text-[#9aa0a6]">
                          <ShoppingBag className="h-4 w-4" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="font-extrabold text-[#0e0e10] truncate max-w-[260px]">{p.name}</div>
                        <div className="font-mono text-[10.5px] text-[#9aa0a6] truncate max-w-[260px]">{p.slug}</div>
                      </div>
                      {p.featured && (
                        <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full bg-[#fff8e7] text-[#a8871a] border border-[#c9a227]/30 shrink-0">
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 capitalize text-[#3a3a3f]">{p.category.replace('-', ' ')}</td>
                  <td className="px-4 py-2.5 text-right font-extrabold tabular-nums text-[#0e0e10]">
                    {p.raw_price > 0 ? fmt$(p.adjusted_price) : <span className="text-emerald-700">Free</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#6E6E73]">
                    {p.raw_price > 0 ? fmt$(p.raw_price) : '—'}
                    {p.adjusted_price !== p.raw_price && p.raw_price > 0 && (
                      <span className="ml-1 text-[10px] font-bold text-[#a8871a]">+${rule.handling_fee || 45}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {p.stock === 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider rounded-full bg-red-50 text-red-700 border border-red-200">
                        <XCircle className="h-3 w-3" /> Out
                      </span>
                    ) : p.low_stock ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                        <AlertTriangle className="h-3 w-3" /> {p.stock}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> {p.stock}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {p.rating ? (
                      <span className="inline-flex items-center gap-1 text-[#0e0e10] font-bold">
                        <Star className="h-3 w-3 fill-[#f5a623] text-[#f5a623]" />
                        {p.rating}
                        <span className="text-[#9aa0a6] font-medium">({p.reviews || 0})</span>
                      </span>
                    ) : (
                      <span className="text-[#9aa0a6]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <a
                      href={`/services/p/${encodeURIComponent(p.slug)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`catalog-open-${p.slug}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#E5E5E7] hover:bg-[#0e0e10] hover:text-white hover:border-[#0e0e10] transition-colors"
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, tone = 'default' }) {
  const toneCls =
    tone === 'ok'   ? 'bg-emerald-50 text-emerald-700' :
    tone === 'warn' ? 'bg-amber-50 text-amber-700' :
    tone === 'mute' ? 'bg-[#F5F5F7] text-[#6E6E73]' :
                      'bg-[#fff8e7] text-[#a8871a]';
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
      <div className="flex items-center gap-3 mb-2">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6E6E73]">{label}</div>
      </div>
      <div className="text-[22px] md:text-[26px] font-extrabold tabular-nums text-[#0e0e10] leading-none">
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-[#6E6E73] mt-1">
          <BarChart3 className="inline-block w-3 h-3 -mt-0.5 mr-1 opacity-60" />
          {hint}
        </div>
      )}
    </div>
  );
}


/* Low-stock digest config + manual fire button. Fetches the schedule + recipient
 * from the backend so admins know exactly what will happen at the next cron tick. */
function LowStockDigestCard({ threshold }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        fetch(`${API}/admin/services/low-stock`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/admin/services/digest-log?limit=10`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
      ]);
      if (p) setData(p);
      if (h) setHistory(h.rows || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendNow = useCallback(async () => {
    setSending(true);
    setSent(false);
    try {
      const r = await fetch(`${API}/admin/services/low-stock/send-now`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        setSent(true);
        // Refresh state so 'Last fired' line + history reflect the new run.
        load();
      }
    } finally {
      setSending(false);
      setTimeout(() => setSent(false), 4000);
    }
  }, [load]);

  const out = data?.out_of_stock?.length ?? 0;
  const low = data?.low_stock?.length ?? 0;
  const total = out + low;
  const recipient = data?.recipient || 'careers@zhelta.com';
  const hour = data?.schedule?.hour ?? 9;
  const tz = data?.schedule?.tz || 'UTC';
  const last = data?.last_run;
  const lastAgo = last?.ts ? fmtAgo(last.ts) : null;

  return (
    <section
      data-testid="services-low-stock-card"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl shrink-0
            ${total > 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}
          >
            {total > 0 ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">
              Low-stock email digest
            </div>
            <div className="text-[15px] font-extrabold text-[#0e0e10] mt-0.5">
              {loading ? 'Checking catalog…'
                : total > 0
                  ? `${total} item${total === 1 ? '' : 's'} need restocking`
                  : 'All clear — every product is healthily stocked'}
            </div>
            <p className="text-[12px] text-[#6E6E73] mt-1 leading-snug">
              Fires automatically at <span className="font-bold text-[#0e0e10]">{String(hour).padStart(2, '0')}:00 {tz}</span>{' '}
              to <span className="font-bold text-[#0e0e10]">{recipient}</span>{' '}
              when any product hits stock ≤ <span className="font-bold text-[#0e0e10]">{threshold}</span>.
              Silently no-ops on green days.
            </p>
            {total > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {out > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">
                    <XCircle className="h-3 w-3" /> {out} out
                  </span>
                )}
                {low > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                    <AlertTriangle className="h-3 w-3" /> {low} low
                  </span>
                )}
              </div>
            )}
            {last && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#6E6E73]" data-testid="low-stock-last-run">
                <Clock className="h-3 w-3" />
                <span>
                  Last digest: <span className="font-bold text-[#0e0e10]">{lastAgo}</span>
                  {' · '}
                  <DigestStatusChip status={last.status} flagged={last.total_flagged} />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end flex-wrap">
          <button
            type="button"
            onClick={load}
            data-testid="low-stock-refresh"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
          >
            <Clock className="w-3 h-3" /> Re-check
          </button>
          <button
            type="button"
            onClick={sendNow}
            disabled={sending || total === 0}
            data-testid="low-stock-send-now"
            className={`inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all
              ${total === 0 || sending
                ? 'bg-[#F5F5F7] text-[#9aa0a6] cursor-not-allowed'
                : 'text-[#0e0e10] hover:-translate-y-0.5'
              }`}
            style={total > 0 && !sending ? { background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' } : {}}
            title={total === 0 ? 'No items to digest right now' : `Send digest to ${recipient}`}
          >
            {sending
              ? <><RefreshCw className="w-3 h-3 animate-spin" /> Sending…</>
              : sent
                ? <><Mail className="w-3 h-3" /> Sent!</>
                : <><Send className="w-3 h-3" /> Send digest now</>}
          </button>
          {history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="low-stock-history-toggle"
              className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
            >
              {showHistory ? 'Hide' : 'Show'} history ({history.length})
            </button>
          )}
        </div>
      </div>
      {showHistory && history.length > 0 && (
        <div className="border-t border-gray-100 bg-[#FAFAFA]" data-testid="low-stock-history">
          <ul className="divide-y divide-gray-100">
            {history.map((row, idx) => (
              <li
                key={`${row.ts}-${idx}`}
                className="px-5 py-2.5 grid grid-cols-[auto_1fr_auto] items-center gap-3 text-[12px]"
              >
                <DigestStatusChip status={row.status} flagged={row.total_flagged} />
                <div className="min-w-0">
                  <div className="font-bold text-[#0e0e10] truncate">
                    {row.status === 'sent'
                      ? `Sent to ${row.recipient || 'support'} · ${row.total_flagged} flagged`
                      : row.status === 'skipped_no_items'
                        ? 'Catalog healthy — no email sent'
                        : `Skipped — ${row.error || 'unknown error'}`}
                  </div>
                  {row.sample_slugs?.length > 0 && (
                    <div className="text-[10.5px] text-[#9aa0a6] font-mono truncate mt-0.5">
                      {row.sample_slugs.join(' · ')}
                    </div>
                  )}
                </div>
                <span className="text-[10.5px] text-[#9aa0a6] tabular-nums shrink-0">
                  {fmtAgo(row.ts)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DigestStatusChip({ status, flagged }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
        <Mail className="h-3 w-3" /> Sent · {flagged}
      </span>
    );
  }
  if (status === 'skipped_no_items') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F5F5F7] text-[#6E6E73] border border-[#E5E5E7]">
        <CheckCircle2 className="h-3 w-3" /> All clear
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
      <AlertTriangle className="h-3 w-3" /> Skipped
    </span>
  );
}
